import { useEffect, useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import axiosInstance from "../../api/axiosInstance";

// Centralized Tailwind classes
const styles = {
  wrapper: "!p-6 text-left",
  cardGrid: "grid grid-cols-1 !gap-5",
  card: "bg-white rounded-lg !p-6 shadow-md border border-gray-200 !space-y-2",
  status: "!px-4 !py-1 bg-gray-800 text-white rounded w-max uppercase text-sm tracking-wide",
  mainGrid: "grid grid-cols-1 md:grid-cols-4 !gap-3 text-gray-700",
  toggleButton: "flex items-center justify-between w-full text-left cursor-pointer bg-gray-200 hover:bg-gray-300/80 !px-4 !py-2 rounded-md",
  toggleContent: "!space-y-0 !p-2 bg-gray-100/70 rounded-b-md",

  // Skeleton
  skeletonCard: "bg-white rounded-lg !p-6 shadow-md border border-gray-200 !space-y-4 animate-pulse",
  skeletonBar: "!h-4 w-full bg-gray-300 rounded",
  skeletonHeader: "!h-6 !w-24 bg-gray-300 rounded",
  skeletonButton: "!h-10 w-full bg-gray-300 rounded",

  // Pagination
  paginationWrapper: "flex flex-col items-center !mb-10 mt-4",
  paginationText: "text-sm text-gray-700",
  paginationBtn: "flex items-center justify-center !px-4 h-10 text-base font-medium text-white cursor-pointer",
  btnPrev: "rounded-s bg-[#008080] hover:bg-[#009999] disabled:bg-[#006666]",
  btnNext: "rounded-e bg-[#008080] hover:bg-[#009999] disabled:bg-[#006666]",
};

const LeadsS = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const initialPage = Number(searchParams.get("page")) || 1;
  const initialLimit = Number(searchParams.get("limit")) || 24;

  const [page, setPage] = useState(initialPage); // eslint-disable-next-line
  const [limit, setLimit] = useState(initialLimit);

  const [loading, setLoading] = useState(false);
  const [totalLeads, setTotalLeads] = useState(1);
  const [leads, setLeads] = useState([]);

  const [openLeadDetails, setOpenLeadDetails] = useState({});
  const [openPremiumDetails, setOpenPremiumDetails] = useState({});

  const toggleLeadDetails = (id) => {
    setOpenLeadDetails((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const togglePremiumDetails = (id) => {
    setOpenPremiumDetails((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const indexOfLastLead = page * limit;
  const indexOfFirstLead = indexOfLastLead - limit;
  const totalPages = Math.ceil(totalLeads / limit);

  const fetchLeads = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`api/webhooks/leads-s-2?limit=${limit}&page=${page}`);

      if (response.status === 200) {
        setLeads(response.data.data || []);
        setTotalLeads(response.data.total || 1);
      }
    } catch (error) {
      console.error("error fetching leads.", error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeads(); // eslint-disable-next-line
  }, [page, limit]);

  const changePage = (newPage) => {
    if (newPage < 1 || newPage > totalPages) return;

    setPage(newPage);
    setSearchParams({ page: newPage, limit });
  };

  const getPaginationButtons = () => {
    const buttons = [];
    const maxVisiblePages = 5;

    buttons.push(
      <button key={1} className={`flex items-center justify-center !px-4 h-10 text-base font-medium text-white rounded-none cursor-pointer ${page === 1 ? "bg-[#006666]" : "bg-[#008080] hover:bg-[#009999]"}`} onClick={() => changePage(1)}>
        1
      </button>
    );

    let startPage = Math.max(2, page - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages - 1, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(2, endPage - maxVisiblePages + 1);
    }

    if (startPage > 2) {
      buttons.push(
        <span key="ellipsis-start" className="flex items-center justify-center !px-4 h-10 text-base font-medium text-white cursor-default bg-[#008080]">
          ...
        </span>
      );
    }

    for (let i = startPage; i <= endPage; i++) {
      buttons.push(
        <button key={i} className={`flex items-center justify-center !px-4 h-10 text-base font-medium text-white cursor-pointer ${page === i ? "bg-[#006666]" : "bg-[#008080] hover:bg-[#009999]"}`} onClick={() => changePage(i)}>
          {i}
        </button>
      );
    }

    if (endPage < totalPages - 1) {
      buttons.push(
        <span key="ellipsis-end" className="flex items-center justify-center !px-4 h-10 text-base font-medium text-white cursor-default bg-[#008080]">
          ...
        </span>
      );
    }

    if (totalPages > 1) {
      buttons.push(
        <button key={totalPages} className={`flex items-center justify-center !px-4 h-10 text-base font-medium text-white rounded-none cursor-pointer ${page === totalPages ? "bg-[#006666]" : "bg-[#008080] hover:bg-[#009999]"}`} onClick={() => changePage(totalPages)}>
          {totalPages}
        </button>
      );
    }

    return buttons;
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.cardGrid}>
        {loading ? (
          <>
            {[...Array(4)].map((_, idx) => (
              <div key={idx} className={styles.skeletonCard}>
                <div className={styles.skeletonHeader}></div>

                <div className="grid grid-cols-1 md:grid-cols-4 !gap-3">
                  {[...Array(12)].map((__, i) => (
                    <div key={i} className={styles.skeletonBar}></div>
                  ))}
                </div>

                <div className={styles.skeletonButton}></div>
                <div className={styles.skeletonButton}></div>
              </div>
            ))}
          </>
        ) : (
          <>
            {leads.map((lead) => (
              <div key={lead.lead_id} className={styles.card}>
                <p
                  className={`
    !px-4 !py-1 rounded w-max uppercase text-sm tracking-wide
    ${lead.status === "exclusive" ? "bg-gray-800 text-white" : lead.status === "active" ? "bg-green-600 text-white" : lead.status === "sale" ? "bg-red-600 text-white" : "bg-gray-500 text-white"}
  `}
                >
                  {lead.status} Lead
                </p>

                <div className={styles.mainGrid}>
                  <p>
                    <span className="font-semibold">Lead Added: </span>
                    {new Date(lead.date_created).toLocaleString("en-US", {
                      day: "2-digit",
                      month: "long",
                      year: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                      hour12: true,
                    })}
                  </p>

                  <p>
                    <span className="font-semibold">Source: </span>
                    {lead.source}
                  </p>
                  <p>
                    <span className="font-semibold">Address: </span>
                    {lead.formatted_address}
                  </p>
                  <p>
                    <span className="font-semibold">Property Type: </span>
                    {lead.property_type}
                  </p>
                  <p>
                    <span className="font-semibold">Beds: </span>
                    {lead.bedrooms}
                  </p>
                  <p>
                    <span className="font-semibold">Baths: </span>
                    {lead.bathrooms}
                  </p>
                  <p>
                    <span className="font-semibold">Buyers in city: </span>
                    {lead.buyers_in_city}
                  </p>
                  <p>
                    <span className="font-semibold">Buyers in county: </span>
                    {lead.buyers_in_county}
                  </p>
                  <p>
                    <span className="font-semibold">Lead Price: </span>
                    {lead.lead_price}
                  </p>
                  <p>
                    <span className="font-semibold">Lead Sale Price: </span>
                    {lead.lead_sale_price}
                  </p>
                  <p>
                    <span className="font-semibold">Property Price: </span>
                    {lead.price}
                  </p>
                  <p>
                    <span className="font-semibold">Property Price range low: </span>
                    {lead.price_range_low}
                  </p>
                  <p>
                    <span className="font-semibold">Property Price range high: </span>
                    {lead.price_range_high}
                  </p>
                  <p className="md:col-span-2">
                    <span className="font-semibold">Goal: </span> {lead.goal}
                  </p>
                  <p className="md:col-span-4">
                    <span className="font-semibold">Predictor Summary: </span>
                    {lead.predictor_summary}
                  </p>
                </div>

                <div className="!pt-3">
                  <button onClick={() => toggleLeadDetails(lead.lead_id)} className={styles.toggleButton}>
                    <span className="text-gray-800 font-semibold">Lead Details</span>
                    {openLeadDetails[lead.lead_id] ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                  </button>

                  {openLeadDetails[lead.lead_id] && (
                    <div className={styles.toggleContent}>
                      {lead.lead_details.map((item, idx) => (
                        <div key={idx} className="flex justify-start !px-2 rounded !space-x-2">
                          <span className="font-semibold capitalize">{item.field}: </span>
                          <span>{item.value}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="!pt-3">
                  <button onClick={() => togglePremiumDetails(lead.lead_id)} className={styles.toggleButton}>
                    <span className="text-gray-800 font-semibold">Premium Details</span>
                    {openPremiumDetails[lead.lead_id] ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                  </button>

                  {openPremiumDetails[lead.lead_id] && (
                    <div className={styles.toggleContent}>
                      {lead.premium_details.map((item, idx) => (
                        <div key={idx} className="flex justify-start !px-2 rounded !space-x-2">
                          <span className="font-semibold capitalize">{item.field}: </span>
                          <span>{item.value}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </>
        )}
      </div>

      {totalLeads > 0 && (
        <div className="flex flex-col items-center !mb-10 !mt-4">
          <span className="text-sm text-gray-700">
            Showing <span className="font-semibold text-gray-900">{indexOfFirstLead + 1}</span> to <span className="font-semibold text-gray-900">{Math.min(indexOfLastLead, totalLeads)}</span> of <span className="font-semibold text-gray-900">{totalLeads}</span> Entries
          </span>

          <div className="inline-flex !mt-2 xs:mt-0">
            <button className="flex items-center justify-center !px-4 h-10 text-base font-medium text-white rounded-s cursor-pointer bg-[#008080] hover:bg-[#009999] disabled:bg-[#006666]" onClick={() => changePage(page - 1)} disabled={page === 1}>
              Prev
            </button>

            {getPaginationButtons()}

            <button className="flex items-center justify-center !px-4 h-10 text-base font-medium text-white rounded-e cursor-pointer bg-[#008080] hover:bg-[#009999] disabled:bg-[#006666]" onClick={() => changePage(page + 1)} disabled={page === totalPages}>
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeadsS;
